import type { DualMontagePreference } from "../components/customization/customizationTypes";
import type { DockPanelKey } from "../components/dockPanelLabels";
import type {
  RowHeightMode,
  WaveformDisplayMode,
} from "../components/eegViewerDisplayHelpers";

export type DisplayProfileOwnerScope = "user" | "org";
export type DisplayProfileGridDensity = "low" | "medium" | "high";
export type DisplayProfileBackgroundTheme = "light" | "dark" | "high_contrast";

export type DisplayProfileConfig = {
  montage_id?: string;
  sensitivity_value?: number;
  time_scale?: number;
  high_pass_filter?: number;
  low_pass_filter?: number;
  notch_filter_60hz?: boolean;
  notch_filter_50hz?: boolean;
  remove_dc_offset?: boolean;
  polarity?: "negative-up" | "positive-up";
  channel_color_mode?: "uniform" | "alternating" | "by-type";
  reference_type?: string;
  playback_speed?: number;
  display_mode?: WaveformDisplayMode;
  row_height_mode?: RowHeightMode;
  allow_trace_overflow?: boolean;
  refit_on_channel_change?: boolean;
  channel_colors?: Record<string, string>;
  waveform_thickness?: number;
  channel_spacing?: number;
  label_font_size?: number;
  channel_position_offsets?: Record<string, number>;
  grid_density?: DisplayProfileGridDensity;
  background_theme?: DisplayProfileBackgroundTheme;
  trend_threshold_profile_id?: string | null;
  dual_montage_preference?: Partial<DualMontagePreference>;
  dock_layout?: {
    bottom_height_px?: number;
    right_width_px?: number;
    clinical_width_px?: number;
    bottom_collapsed?: boolean;
    right_collapsed?: boolean;
    active_bottom_tab?: DockPanelKey | string;
  };
  panel_visibility?: Partial<Record<DockPanelKey, boolean>>;
  panel_order?: DockPanelKey[];
  crosshair_enabled?: boolean;
  show_grid?: boolean;
  show_channel_group_labels?: boolean;
  show_scale_markers?: boolean;
  hidden_channels?: string[];
  [key: string]: unknown;
};

export type EEGDisplayProfile = {
  profile_id: string;
  owner_scope: DisplayProfileOwnerScope;
  owner_id: string;
  profile_name: string;
  config_json: DisplayProfileConfig;
  is_default: boolean;
  version: number;
  updated_at: string;
};

export type DisplayProfilesResponse = {
  profiles: EEGDisplayProfile[];
  effective_default_profile_id: string | null;
};

export type DisplayProfileNumericPolicy = {
  min: number;
  max: number;
  default: number;
  step: number;
};

export type DisplayProfileVisualPolicy = {
  waveform_thickness: DisplayProfileNumericPolicy;
  channel_spacing: DisplayProfileNumericPolicy;
  label_font_size: DisplayProfileNumericPolicy;
  channel_position_offset: DisplayProfileNumericPolicy;
};

export type DisplayProfilePolicyResponse = {
  visual: DisplayProfileVisualPolicy;
  defaults: {
    background_theme: DisplayProfileBackgroundTheme;
    grid_density: DisplayProfileGridDensity;
  };
  playback_speed: {
    options: number[];
    default: number;
  };
};

export const FALLBACK_DISPLAY_PROFILE_POLICY: DisplayProfilePolicyResponse = {
  visual: {
    waveform_thickness: { min: 0.5, max: 6, default: 1.5, step: 0.1 },
    channel_spacing: { min: 0.5, max: 3, default: 1.0, step: 0.1 },
    label_font_size: { min: 8, max: 24, default: 12, step: 1 },
    channel_position_offset: { min: -2, max: 2, default: 0, step: 0.1 },
  },
  defaults: {
    background_theme: "dark",
    grid_density: "medium",
  },
  playback_speed: {
    options: [0.5, 1, 2, 4, 8, 16, 30, 60, 100, 150, 200],
    default: 1,
  },
};

/** Fallback inline speeds when the playback bar cannot fit the full set. */
export const INLINE_PLAYBACK_SPEED_OPTIONS = [0.5, 1, 2, 4, 8, 16] as const;

export const RENDER_THEME_MAP: Record<
  DisplayProfileBackgroundTheme,
  { backgroundColor: string; waveformColor: string; gridColorBase: string }
> = {
  light: {
    backgroundColor: "#ffffff",
    waveformColor: "#0f172a",
    gridColorBase: "#94a3b8",
  },
  dark: {
    backgroundColor: "#000000",
    waveformColor: "#e2e8f0",
    gridColorBase: "#64748b",
  },
  high_contrast: {
    backgroundColor: "#000000",
    waveformColor: "#ffff00",
    gridColorBase: "#ffffff",
  },
};
